<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import AgentsAPI from '../../../../api/agents';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  whatsappPhone: {
    type: String,
    default: '',
  },
  whatsappAvatarSyncStatus: {
    type: String,
    default: 'not_synced',
  },
  whatsappAvatarSyncError: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });
const whatsappPhone = ref(props.whatsappPhone || '');
const whatsappAvatarSyncStatus = ref(props.whatsappAvatarSyncStatus);
const whatsappAvatarSyncError = ref(props.whatsappAvatarSyncError || '');
const isRefreshingWhatsappAvatar = ref(false);
const whatsappFieldLabel = 'WhatsApp do usuário';
const whatsappPhonePlaceholder = '+5533999999999';
const whatsappUnsavedHelp =
  'Salve o usuário antes de sincronizar este novo número.';

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const whatsappSyncLabel = computed(() => {
  const labels = {
    not_synced: 'Ainda não sincronizada',
    queued: 'Sincronização agendada',
    syncing: 'Sincronizando foto…',
    synced: 'Foto sincronizada',
    unavailable: 'Foto indisponível no WhatsApp',
    error: 'Erro ao sincronizar a foto',
  };
  return labels[whatsappAvatarSyncStatus.value] || 'Ainda não sincronizada';
});

const whatsappPhoneSaved = computed(() => {
  return whatsappPhone.value.trim() === (props.whatsappPhone || '').trim();
});

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
      whatsapp_phone_e164: whatsappPhone.value.trim() || null,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const refreshWhatsappAvatar = async () => {
  if (!whatsappPhone.value.trim()) return;
  isRefreshingWhatsappAvatar.value = true;
  try {
    const response = await AgentsAPI.refreshWhatsappAvatar(props.id);
    whatsappAvatarSyncStatus.value = response.data.whatsapp_avatar_sync_status;
    whatsappAvatarSyncError.value =
      response.data.whatsapp_avatar_sync_error || '';
    await store.dispatch('agents/get');
    if (response.data.whatsapp_avatar_sync_status === 'synced') {
      useAlert('Foto do WhatsApp atualizada.');
    } else {
      useAlert(
        whatsappAvatarSyncError.value ||
          'A foto não está disponível no WhatsApp.'
      );
    }
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      'Não foi possível sincronizar a foto do WhatsApp.';
    whatsappAvatarSyncStatus.value = 'error';
    whatsappAvatarSyncError.value = message;
    useAlert(message);
  } finally {
    isRefreshingWhatsappAvatar.value = false;
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label>
          {{ whatsappFieldLabel }}
          <input
            v-model="whatsappPhone"
            type="tel"
            inputmode="tel"
            autocomplete="tel"
            :placeholder="whatsappPhonePlaceholder"
          />
          <span class="message">{{ whatsappSyncLabel }}</span>
          <span v-if="!whatsappPhoneSaved" class="message text-n-amber-11">
            {{ whatsappUnsavedHelp }}
          </span>
          <span v-if="whatsappAvatarSyncError" class="message text-n-ruby-11">
            {{ whatsappAvatarSyncError }}
          </span>
        </label>
        <Button
          type="button"
          faded
          slate
          icon="i-lucide-refresh-cw"
          label="Sincronizar foto agora"
          :disabled="
            !whatsappPhone.trim() ||
            !whatsappPhoneSaved ||
            isRefreshingWhatsappAvatar
          "
          :is-loading="isRefreshingWhatsappAvatar"
          @click.prevent="refreshWhatsappAvatar"
        />
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
